// Campaign attribution is separate from membership/referral tokens. A tagged
// landing is eligible for seven days; ordinary navigation does not renew it.
// First touch is used for new profiles, last touch for subsequent actions.
// With analytics consent, persistence survives auth redirects/new tabs. Without
// it, the existing cookieless behavior is preserved with page-memory only.
import { hasAnalyticsConsent } from "./consentManager";
export const ATTRIBUTION_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;
const STORAGE_KEY = "22n_attribution_v1";
const UTM_PARAMS = ["utm_source", "utm_medium", "utm_campaign", "utm_content"];
let memoryTouches = null;
let previousConsent = null;

const storage = () => {
  try { return window.localStorage; } catch { return null; }
};

export const syncCampaignAttributionConsent = () => {
  const allowed = hasAnalyticsConsent();
  if (!allowed) {
    if (previousConsent === true) memoryTouches = null;
    // Cleanup is permitted on revocation; never read rejected persistent data.
    try { storage()?.removeItem(STORAGE_KEY); } catch { /* blocked storage */ }
  } else if (previousConsent === false && memoryTouches) {
    try { storage()?.setItem(STORAGE_KEY, JSON.stringify(memoryTouches)); } catch { /* memory mode */ }
  }
  previousConsent = allowed;
  return allowed;
};

const validTouch = (touch, now) => {
  const timestamp = touch?.capturedAt;
  return typeof timestamp === "number" && Number.isFinite(timestamp)
    && timestamp <= now && now - timestamp < ATTRIBUTION_WINDOW_MS
    && touch.fields && typeof touch.fields === "object";
};

const readTouches = (now = Date.now()) => {
  let record = memoryTouches;
  try {
    const allowed = syncCampaignAttributionConsent();
    record = allowed ? JSON.parse(storage()?.getItem(STORAGE_KEY) || "null") || memoryTouches : memoryTouches;
  } catch { record = memoryTouches; }
  return {
    firstTouch: validTouch(record?.firstTouch, now) ? record.firstTouch : null,
    lastTouch: validTouch(record?.lastTouch, now) ? record.lastTouch : null,
  };
};

/** Capture on every route, including direct event/deal/account landings. */
export function captureUtmParams(search = window.location.search) {
  try {
    const params = new URLSearchParams(search);
    const fields = {};
    UTM_PARAMS.forEach((key) => {
      const value = params.get(key)?.trim();
      if (value) fields[key] = value;
    });
    const tokenName = params.get("token")?.trim();
    if (!Object.keys(fields).length && !tokenName) return;

    if (fields.utm_source === "email") {
      fields.signup_source = fields.utm_medium === "transactional"
        ? "transactional_email" : "email_campaign";
    } else if (fields.utm_source) {
      fields.signup_source = fields.utm_source;
    } else if (tokenName) {
      fields.signup_source = "token";
    }
    // Marketing touch only. Membership tokens in Redux/localStorage are unchanged.
    if (tokenName) fields.token_name = tokenName;

    const now = Date.now();
    const existing = readTouches(now);
    const touch = { capturedAt: now, fields };
    memoryTouches = {
      firstTouch: existing.firstTouch || touch,
      lastTouch: touch,
    };
    if (syncCampaignAttributionConsent()) storage()?.setItem(STORAGE_KEY, JSON.stringify(memoryTouches));
  } catch { /* Attribution must not interrupt navigation or signups. */ }
}

export function getStoredUtm() {
  return readTouches().lastTouch?.fields || null;
}

const fieldsForTouch = (touch, includeCampaignId) => {
  if (!touch) return {};
  // Explicit column whitelist: User has UTM columns but no attributed_campaign_id.
  const fields = {};
  ["signup_source", ...UTM_PARAMS].forEach((key) => {
    if (typeof touch.fields[key] === "string" && touch.fields[key]) {
      fields[key] = touch.fields[key];
    }
  });
  if (includeCampaignId && fields.signup_source === "email_campaign"
    && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(fields.utm_content || "")) {
    fields.attributed_campaign_id = fields.utm_content;
  }
  return fields;
};

/** Action payload for EventSignUp/LifestyleSignUp, using the last eligible touch. */
export function getAttributionFields() {
  return fieldsForTouch(readTouches().lastTouch, true);
}

/** Carry the observed touch, not a fresh timestamp, across paid checkout. */
export function getCheckoutAttribution() {
  const touch = readTouches().lastTouch;
  if (!touch) return null;
  return {
    ...fieldsForTouch(touch, false),
    ...(touch.fields.token_name ? { token_name: touch.fields.token_name } : {}),
    captured_at: new Date(touch.capturedAt).toISOString(),
  };
}

/** Acquisition payload for a NEW public.User profile; never overwrite an existing one. */
export function getProfileAttributionFields() {
  const touches = readTouches();
  return fieldsForTouch(touches.firstTouch || touches.lastTouch, false);
}

// Explicit reset only; account creation/action success must preserve the touch
// for the rest of its window. Untimestamped legacy storage is not re-credited.
export function clearUtmParams() {
  memoryTouches = null;
  previousConsent = null;
  try { storage()?.removeItem(STORAGE_KEY); } catch { /* unavailable storage */ }
  try { sessionStorage.removeItem("22n_utm"); } catch { /* legacy key */ }
}
